/**
 * [] init response aus backend (backendInformation?)
 * [] weitere response aus elastic search (express backend)
 * [] trainstations | destination | airport
 */

import React, {
    Context,
    createContext,
    ReactNode,
    Reducer,
    useMemo,
    useReducer,
} from 'react';

import { AutocompleteReducer } from './__reducer';
import { buildActionMap } from './__reducer/actionMap';
import {
    AutocompleteActionMap,
    AutocompleteReducerAction,
    AutocompleteState,
} from './types';

export function createAutocompleteStateContext<ResultEntry, Value>() {
    return createContext<AutocompleteState<ResultEntry, Value>>({
        config: {
            searchResultProvider: async () => [],
            threshold: 2,
        },
        currentValue: new Set(),
        lastModified: 0,
        recentValue: new Set(),
    });
}

export function createAutocompleteActionContext<ResultEntry, Value>() {
    return createContext<AutocompleteActionMap<ResultEntry, Value>>({
        setResults: () => {},
        setValue: () => {},
        reset: () => {},
        clear: () => {},
        config: {
            setThreshold: () => {},
        },
    });
}

type ACReducer<ResultEntry, Value> = Reducer<
    AutocompleteState<ResultEntry, Value>,
    AutocompleteReducerAction<ResultEntry, Value>
>;

interface AutocompleteContextProviderProps<ResultEntry, Value> {
    initialState: AutocompleteState<ResultEntry, Value>;
    children: ReactNode;
}

export function createAutocompleteProvider<ResultEntry, Value>(
    stateContext: Context<AutocompleteState<ResultEntry, Value>>,
    actionContext: Context<AutocompleteActionMap<ResultEntry, Value>>
) {
    function AutocompleteContextProvider({
        initialState,
        children,
    }: AutocompleteContextProviderProps<ResultEntry, Value>) {
        const [reducerState, dispatch] = useReducer(
            AutocompleteReducer as ACReducer<ResultEntry, Value>,
            initialState
        );

        const actionMap = useMemo(() => buildActionMap(dispatch), [dispatch]);

        return (
            <actionContext.Provider value={actionMap}>
                <stateContext.Provider value={reducerState}>
                    {children}
                </stateContext.Provider>
            </actionContext.Provider>
        );
    }

    return AutocompleteContextProvider;
}
